import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faPlus, faSave, faTrashAlt, faPen, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import DataService from '../../services/DataService';
import TimeStamp from '../../utils/TimeStamp';
import DeleteModal from '../../components/common/DeleteModal';
import NotificationSystem from '../../components/common/NotificationSystem';
import useNotification from '../../hooks/useNotification';
import Tooltip from '../../components/common/Tooltip';

const ProductList = () => {
    const [products, setProducts] = useState([]);
    const [productTypes, setProductTypes] = useState([]);
    const [newProduct, setNewProduct] = useState(null);
    const [editingProduct, setEditingProduct] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ isActive: false, productId: null });
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [typeFilter, setTypeFilter] = useState('');
    const { notification, showNotification, hideNotification } = useNotification();

    const fetchProducts = useCallback(async () => {
        try {
            const products = await DataService.getAll('products', 0, 0);
            
            // Charger les types de produits si pas encore chargés
            if (productTypes.length === 0) {
                const types = await DataService.getTypes('ProductType');
                setProductTypes(types);
            }

            setProducts(products);
        } catch (error) {
            console.error('Erreur lors de la récupération des produits:', error);
        }
    }, [productTypes.length]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const getProductTypeName = (typeId) => {
        if (!typeId || !productTypes.length) return '';
        // Convertir typeId en nombre pour la comparaison
        const numericTypeId = parseInt(typeId);
        const type = productTypes.find(t => t.id === numericTypeId);
        return type ? type.data : ''; // Utiliser data au lieu de name pour l'affichage
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        const dateStr = TimeStamp.fromString(timestamp).toStandardString().slice(0, 10);
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
    };

    const handleAddNew = () => {
        const today = TimeStamp.fromDate(new Date()).toStandardString().slice(0, 10);
        setNewProduct({
            name: '',
            typeId: productTypes.length > 0 ? productTypes[0].id : '',
            description: '',
            price: 0,
            validFrom: today,
            untilTo: '',
            userProduct: true
        });
    };

    const handleSave = async (product) => {
        // Validation des champs obligatoires
        if (!product.name || !product.name.trim()) {
            showNotification('Le nom du produit est obligatoire', 'error');
            return;
        }

        if (!product.typeId) {
            showNotification('Le type de produit est obligatoire', 'error');
            return;
        }

        try {
            // Gérer les dates existantes si elles ne sont pas modifiées
            const validFromDate = product.validFrom ? 
                (typeof product.validFrom === 'string' && !product.validFrom.includes('-') ? 
                    TimeStamp.fromDate(new Date(`${product.validFrom}T00:00`)).toString() :
                    product.validFrom) : 
                null;

            const untilToDate = product.untilTo ? 
                (typeof product.untilTo === 'string' && !product.untilTo.includes('-') ? 
                    TimeStamp.fromDate(new Date(`${product.untilTo}T00:00`)).toString() :
                    product.untilTo) : 
                null;

            const productData = {
                ...product,
                validFrom: validFromDate,
                untilTo: untilToDate,
                price: parseFloat(product.price)
            };

            if (product.id) {
                await DataService.update('products', product.id, productData);
            } else {
                await DataService.add('products', productData);
            }
            setNewProduct(null);
            setEditingProduct(null);
            fetchProducts();
            showNotification('Produit sauvegardé avec succès', 'success');
        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
            showNotification('Erreur lors de la sauvegarde: ' + error.message, 'error');
        }
    };

    const handleDelete = (id) => {
        setDeleteModal({ isActive: true, productId: id });
    };

    const confirmDelete = async () => {
        try {
            await DataService.delete('products', deleteModal.productId);
            fetchProducts();
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
        }
        setDeleteModal({ isActive: false, productId: null });
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedProducts = () => {
        const filteredProducts = typeFilter
            ? products.filter(p => String(p.typeId) === typeFilter)
            : products;

        if (!sortConfig.key) return filteredProducts;

        return [...filteredProducts].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.key) {
                case 'name':
                    aValue = a.name;
                    bValue = b.name;
                    break;
                case 'type':
                    aValue = getProductTypeName(a.typeId);
                    bValue = getProductTypeName(b.typeId);
                    break;
                case 'description':
                    aValue = a.description || '';
                    bValue = b.description || '';
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return faSort;
        }
        return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
    };

    const renderProductRow = (product, isEditing, currentProduct) => {
        const dateToInputFormat = (timestamp) => {
            if (!timestamp) return '';
            return TimeStamp.fromString(timestamp).toStandardString().slice(0, 10); // Modifié pour ne prendre que la date
        };
    
        return (
            <tr key={product ? product.id : 'new'}>
                <td>
                    <input
                        className="input is-small"
                        type="text"
                        value={currentProduct?.name || ''}
                        onChange={(e) => isEditing ?
                            setEditingProduct({...currentProduct, name: e.target.value}) :
                            setNewProduct({...currentProduct, name: e.target.value})}
                        disabled={currentProduct?.userProduct !== true}
                    />
                </td>
                <td>
                    <div className="select is-small is-fullwidth">
                        <select
                            value={currentProduct?.typeId || ''}
                            onChange={(e) => isEditing ?
                                setEditingProduct({...currentProduct, typeId: e.target.value}) :
                                setNewProduct({...currentProduct, typeId: e.target.value})}
                            disabled={currentProduct?.userProduct !== true}
                        >
                            <option value="">Sélectionner un type</option>
                            {productTypes.map(type => (
                                <option key={type.id} value={type.id}>
                                    {type.data}
                                </option>
                            ))}
                        </select>
                    </div>
                </td>
                <td>
                    <input
                        className="input is-small"
                        type="text"
                        value={currentProduct?.description || ''}
                        onChange={(e) => isEditing ?
                            setEditingProduct({...currentProduct, description: e.target.value}) :
                            setNewProduct({...currentProduct, description: e.target.value})}
                    />
                </td>
                <td>
                    <input
                        className="input is-small"
                        type="text"
                        pattern="\d*\.?\d*"
                        value={currentProduct?.price || ''}
                        onChange={(e) => {
                            const value = e.target.value.replace(/[^\d.]/g, '');
                            if (isEditing) {
                                setEditingProduct({...currentProduct, price: value});
                            } else {
                                setNewProduct({...currentProduct, price: value});
                            }
                        }}
                    />
                </td>
                <td>
                    <input
                        className="input is-small"
                        type="date" // Changé de datetime-local à date
                        value={isEditing ? dateToInputFormat(currentProduct?.validFrom) : currentProduct?.validFrom}
                        onChange={(e) => isEditing ?
                            setEditingProduct({...currentProduct, validFrom: e.target.value}) :
                            setNewProduct({...currentProduct, validFrom: e.target.value})}
                        disabled={currentProduct?.userProduct !== true}
                    />
                </td>
                <td>
                    <input
                        className="input is-small"
                        type="date" // Changé de datetime-local à date
                        value={isEditing ? dateToInputFormat(currentProduct?.untilTo) : currentProduct?.untilTo}
                        onChange={(e) => isEditing ?
                            setEditingProduct({...currentProduct, untilTo: e.target.value}) :
                            setNewProduct({...currentProduct, untilTo: e.target.value})}
                        disabled={currentProduct?.userProduct !== true}
                    />
                </td>
                <td>
                    <div className="table-actions-container">
                        <button
                            className="button is-small is-success table-action-button"
                            onClick={() => handleSave(currentProduct)}
                        >
                            <span className="icon is-small icon-primary">
                                <FontAwesomeIcon icon={faSave} />
                            </span>
                        </button>
                    </div>
                </td>
            </tr>
        );
    };

    return (
        <div className="panel">
            {notification.isVisible && (
                <NotificationSystem
                    message={notification.message}
                    type={notification.type}
                    duration={3000}
                    onClose={() => hideNotification()}
                />
            )}
            <div className="panel-heading">
                <div className="panel-header-content">
                    <span className="icon-text">
                        <span className="icon">
                            <FontAwesomeIcon icon={faBox} />
                        </span>
                        <span> Produits</span>
                    </span>
                    <div className="panel-header-actions">
                        
                            <select
                                className="select is-small type-filter-select"
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                            >
                                <option value="">Tous les types</option>
                                {productTypes.map(type => (
                                    <option key={type.id} value={type.id}>
                                        {type.data}
                                    </option>
                                ))}
                            </select>
                        
                        <button 
                            className="button is-small"
                            onClick={handleAddNew}
                        >
                            <span className="icon is-small icon-primary">
                                <FontAwesomeIcon icon={faPlus} />
                            </span>
                        </button>
                    </div>
                </div>
            </div>
            <div className="panel-block">
                <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto', width: '100%' }}>
                    <table className="table is-fullwidth is-striped is-hoverable mb-0">
                        <thead>
                            <tr>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('name')}
                                    >
                                        Nom <FontAwesomeIcon icon={getSortIcon('name')} className="ml-1" />
                                    </button>
                                </th>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('type')}
                                    >
                                        Type <FontAwesomeIcon icon={getSortIcon('type')} className="ml-1" />
                                    </button>
                                </th>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('description')}
                                    >
                                        Description <FontAwesomeIcon icon={getSortIcon('description')} className="ml-1" />
                                    </button>
                                </th>
                                <th>Prix</th>
                                <th>Date début</th>
                                <th>Date fin</th>
                                <th className="has-text-right">
                                    <Tooltip content={
                                        <div>
                                            <strong>Actions disponibles</strong>
                                            <div className="workflow-columns">
                                                <div className="workflow-column">
                                                    <h4>Modifier</h4>
                                                    <ul>
                                                        <li>• Éditer les informations</li>
                                                        <li>• Modifier les prix</li>
                                                        <li>• Ajuster les dates</li>
                                                    </ul>
                                                </div>
                                                <div className="workflow-column">
                                                    <h4>Supprimer</h4>
                                                    <ul>
                                                        <li>• Retirer du catalogue</li>
                                                        <li>• Action irréversible</li>
                                                        <li>• Confirmation requise</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    }>
                                        Actions
                                    </Tooltip>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {newProduct && renderProductRow(null, false, newProduct)}
                            {getSortedProducts().map(product => (
                                editingProduct && editingProduct.id === product.id ?
                                renderProductRow(product, true, editingProduct) :
                                <tr key={product.id}>
                                    <td>{product.name}</td>
                                    <td>{getProductTypeName(product.typeId)}</td>
                                    <td>{product.description}</td>
                                    <td className="has-text-right">{product.price.toFixed(2)} €</td>
                                    <td>{formatDate(product.validFrom)}</td>
                                    <td>{formatDate(product.untilTo)}</td>
                                    <td>
                                        <div className="table-actions-container">
                                            <button
                                                className="button is-small table-action-button"
                                                onClick={() => setEditingProduct({...product})}
                                            >
                                                <span className="icon is-small icon-primary">
                                                    <FontAwesomeIcon icon={faPen} />
                                                </span>
                                            </button>
                                            {product.userProduct && (
                                                <button
                                                    className="button is-small table-action-button"
                                                    onClick={() => handleDelete(product.id)}
                                                >
                                                    <span className="icon is-small">
                                                        <FontAwesomeIcon icon={faTrashAlt} className="icon-danger" />
                                                    </span>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="panel-footer"></div>
            <DeleteModal 
                isActive={deleteModal.isActive}
                onClose={() => setDeleteModal({ isActive: false, productId: null })}
                onConfirm={confirmDelete}
                itemName="produit"
            />
        </div>
    );
};

export default ProductList;
